import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { AuthContext, AuthData } from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { getDateErrors, getReadableName } from '../../util/util';
import FieldLabel from '../fields/FieldLabel';
import DefaultDatePicker, {
	DefaultDatePickerValue,
} from '../form/DefaultDatePicker';
import LargeCenterSpinner from '../LargeCenterSpinner';
import ReactSelect from '../policy/fields/ReactSelect';
import ReportTable from './ReportTable';

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
		marginTop: '20px',
	},
	formControl: {
		minWidth: 300,
		maxWidth: 500,
	},
	selectProducts: {
		'&&': {
			marginTop: '0px',
		},
	},
}));

interface Props {
	configData: ConfigData;
	authData: AuthData;
	reportResults: any;
	loadingResults: boolean;
	onUpdate: (reqObj: any, hasError: boolean) => void;
}

const AllPoliciesReport = (props: Props) => {
	const classes = useStyles();
	const { configData, authData, onUpdate, loadingResults, reportResults } =
		props;
	const { productConfigs } = configData;
	const [reportStartTimestamp, setReportStartTimestamp] =
		useState<DefaultDatePickerValue>(null);
	const [reportEndTimestamp, setReportEndTimestamp] =
		useState<DefaultDatePickerValue>(null);
	const [products, setProducts] = useState([]);
	const startDateErrors = getDateErrors(
		reportStartTimestamp,
		null,
		reportEndTimestamp as Date,
		true,
		authData,
	);
	const endDateErrors = getDateErrors(
		reportEndTimestamp,
		reportStartTimestamp as Date,
		null,
		true,
		authData,
	);

	const latestVersion = productConfigs.find(
		(p) => p.name === 'global',
	).configVersion;
	const latestProducts = productConfigs.filter(
		(p) => p.configVersion === latestVersion,
	);
	const productNames = latestProducts
		.map((p) => p.name)
		.filter((name) => name !== 'global');
	const selectedProductNames = products.map((p) => p.value);

	const updateStartDate = (date: DefaultDatePickerValue) => {
		const validDate = date && !isNaN(date.getTime());
		setReportStartTimestamp(date);
		const req = {
			reportStartTimestamp: validDate ? `${(date as Date).getTime()}` : null,
			reportEndTimestamp: reportEndTimestamp
				? `${reportEndTimestamp.getTime()}`
				: null,
			productNames: selectedProductNames,
		};
		// get updated errors to send back to reportsView
		const hasErrors = getDateErrors(
			date,
			null,
			reportEndTimestamp as Date,
			true,
			authData,
		);
		onUpdate(req, !hasErrors.isValid || !endDateErrors.isValid);
	};

	const updateEndDate = (date: DefaultDatePickerValue) => {
		const validDate = date && !isNaN(date.getTime());
		setReportEndTimestamp(date);
		const req = {
			reportStartTimestamp: reportStartTimestamp
				? `${reportStartTimestamp.getTime()}`
				: null,
			reportEndTimestamp: validDate ? `${(date as Date).getTime()}` : null,
			productNames: selectedProductNames,
		};
		// get updated errors to send back to reportsView
		const hasErrors = getDateErrors(
			date,
			reportStartTimestamp as Date,
			null,
			true,
			authData,
		);
		onUpdate(req, !startDateErrors.isValid || !hasErrors.isValid);
	};

	const updateProducts = (opt: any) => {
		setProducts(opt);
		const productNamesArr = opt.map((p: any) => p.value);
		const req = {
			reportStartTimestamp: reportStartTimestamp
				? `${reportStartTimestamp.getTime()}`
				: null,
			reportEndTimestamp: reportEndTimestamp
				? `${reportEndTimestamp.getTime()}`
				: null,
			productNames: productNamesArr,
		};
		onUpdate(req, !startDateErrors.isValid || !endDateErrors.isValid);
	};

	const productNameOptions = productNames.map((productName) => ({
		value: productName,
		label: getReadableName(productName),
	}));

	const renderReportDetails = () => {
		return (
			<Grid container className={classes.root}>
				<Grid item xs={12}>
					<Grid container justifyContent='center'>
						<DefaultDatePicker
							disabled={!!reportResults || loadingResults}
							error={!startDateErrors.isValid}
							errorMessage={
								!startDateErrors.isValid ? startDateErrors.message : null
							}
							id='from'
							showCalendar
							label='Policies Issued From'
							style={{ marginRight: '10px' }}
							value={reportStartTimestamp}
							maxDate={reportEndTimestamp}
							onChange={(date) => updateStartDate(date)}
							required
						/>
						<DefaultDatePicker
							disabled={!!reportResults || loadingResults}
							id='to'
							error={!endDateErrors.isValid}
							errorMessage={
								!endDateErrors.isValid ? endDateErrors.message : null
							}
							label='To'
							showCalendar
							style={{ marginRight: '10px' }}
							value={reportEndTimestamp}
							minDate={reportStartTimestamp}
							onChange={(date) => updateEndDate(date)}
							required
						/>
						<FormControl className={classes.formControl}>
							<FieldLabel
								title='Select Products'
								id='all-policies-product-select'
								required
							/>
							<ReactSelect
								className={classes.selectProducts}
								options={productNameOptions}
								value={products}
								onChange={updateProducts}
								isDisabled={!!reportResults || loadingResults}
								isMulti
							/>
						</FormControl>
					</Grid>
				</Grid>
			</Grid>
		);
	};

	return (
		<>
			{renderReportDetails()}
			{loadingResults && <LargeCenterSpinner />}
			{reportResults && <ReportTable results={reportResults} />}
		</>
	);
};

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<Component {...props} configData={configData} authData={authData} />
					)}
				</ConfigContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(AllPoliciesReport);
